import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogProps,
  Typography,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import type { DriveStep } from 'driver.js';
import { useModalTour, ModalTourButton } from '../hooks/useModalTour';

const ACCENT = '#2C3E2D';
const BORDER = '1px solid #E2DDD8';
const BORDER_STRONG = '2px solid #1A1A1A';

export interface FormDialogProps extends Omit<DialogProps, 'onClose'> {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  isEditing?: boolean;
  editingName?: string;
  accentColor?: string;
  showAccentBorder?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Step definitions for the in-modal form guide (shown only while open). */
  tourSteps?: DriveStep[];
  tourKey?: string;
}

export function FormDialog({
  open,
  onClose,
  title,
  subtitle,
  isEditing,
  editingName,
  accentColor = ACCENT,
  showAccentBorder = true,
  children,
  footer,
  tourSteps,
  tourKey,
  PaperProps,
  ...props
}: FormDialogProps) {
  const tourEnabled = open && !!tourSteps?.length && !!tourKey;
  const { startTour, hasSteps } = useModalTour({
    steps: tourSteps ?? [],
    tourKey: tourKey ?? '',
    enabled: tourEnabled,
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          overflow: 'hidden',
          bgcolor: '#FFFFFF',
          border: '1px solid #c4bfb8',
          ...PaperProps?.sx,
        },
        ...PaperProps,
      }}
      {...props}
    >
      {showAccentBorder && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            bgcolor: accentColor,
            zIndex: 1,
          }}
        />
      )}

      {isEditing && (
        <Box
          px={3}
          py={1.5}
          sx={{
            bgcolor: '#E3F2FD',
            borderBottom: '1px solid #BBDEFB',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <EditIcon sx={{ fontSize: 14, color: '#0D47A1' }} />
          <Typography variant="caption" sx={{ color: '#0D47A1' }}>
            Editing {editingName && <strong>{editingName}</strong>}
          </Typography>
        </Box>
      )}

      <DialogTitle
        sx={{
          borderBottom: BORDER_STRONG,
          px: 3.5,
          py: 2.5,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          bgcolor: '#FFFFFF',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.4rem',
              color: '#1A1A1A',
              letterSpacing: '-0.02em',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                fontFamily: '"DM Mono", monospace',
                fontSize: '0.67rem',
                color: '#9aaba7',
                mt: 0.5,
                letterSpacing: '0.03em',
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tourEnabled && hasSteps && <ModalTourButton onStart={startTour} />}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              border: '1px solid #c4bfb8',
              borderRadius: 0,
              width: 30,
              height: 30,
              color: '#5a6360',
              '&:hover': { bgcolor: '#f0ede8', color: '#1a1f1e' },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2, pr: 3, bgcolor: '#FFFFFF' }}>
        <Box mt={2}>{children}</Box>
      </DialogContent>

      {footer && (
        <DialogActions
          sx={{
            borderTop: BORDER_STRONG,
            px: 3.5,
            py: 1.75,
            gap: 1,
            bgcolor: '#FFFFFF',
          }}
        >
          {footer}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default FormDialog;
